import React, { useState, useEffect, useRef } from 'react';
import { openLeadInNewTab } from './openLeadInNewTab';
import axios from 'axios';

/**
 * A modal that lets users search for leads by ID, name, or email
 */
const OpenLeadByIdModal = ({ isOpen, onClose }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState('name'); // 'id', 'name', or 'email'
  const [error, setError] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const modalRef = useRef(null);

  // Search debounce timeout reference
  const searchTimeoutRef = useRef(null);
  
  // Handle escape key press
  useEffect(() => {
    if (!isOpen) return;
    
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    
    window.addEventListener('keydown', handleEsc);
    
    return () => {
      window.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen, onClose]);
  
  // Cleanup search timeout on component unmount or when modal closes
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Reset state when modal is closed and focus input when opened
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
      setError('');
      setSearchResults([]);
      setLoading(false);
    } else {
      // Focus the input field when modal opens
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 100);
    }
  }, [isOpen]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!searchQuery.trim()) {
      setError('Please enter a search term');
      return;
    }

    // For name/email search, the form submission is redundant since we have real-time search
    // Just handle ID search directly
    if (searchType === 'id') {
      // Clear any previous errors and results
      setError('');
      setSearchResults([]);
      setLoading(true);
      
      // If searching by ID, just open the lead directly
      openLeadInNewTab(searchQuery);
      setLoading(false);
      onClose();
    }
    // For name/email search, we don't need to do anything on form submit since we're already searching in real-time
  };

  const handleLeadSelect = (leadId) => {
    openLeadInNewTab(leadId);
    setSearchQuery('');
    setSearchResults([]);
    onClose();
  };

  if (!isOpen) return null;
  
  // Handle click outside modal
  const handleClickOutside = (e) => {
    if (modalRef.current && !modalRef.current.contains(e.target)) {
      onClose();
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50"
      onClick={handleClickOutside}
    >
      <div ref={modalRef} className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-800">Find Lead</h2>
          <button 
            onClick={onClose} 
            className="text-gray-500 hover:text-gray-700"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label htmlFor="searchType" className="block text-sm font-medium text-gray-700 mb-2">
              Search By
            </label>
            <div className="flex space-x-2 mb-4">
              <button
                type="button"
                onClick={() => {
                  setSearchType('id');
                  setSearchQuery('');
                  setSearchResults([]);
                  setError('');
                }}
                className={`px-3 py-1 rounded-md text-sm font-medium ${
                  searchType === 'id' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                }`}
              >
                ID
              </button>
              <button
                type="button"
                onClick={() => {
                  setSearchType('name');
                  setSearchQuery('');
                  setSearchResults([]);
                  setError('');
                }}
                className={`px-3 py-1 rounded-md text-sm font-medium ${
                  searchType === 'name' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                }`}
              >
                Name/Email/Phone
              </button>
            </div>
            
            <label htmlFor="searchQuery" className="block text-sm font-medium text-gray-700 mb-2">
              {searchType === 'id' ? 'Lead ID' : 'Search Term'}
            </label>
            <input
              type="text"
              id="searchQuery"
              ref={inputRef}
              value={searchQuery}
              onChange={(e) => {
                const newValue = e.target.value;
                setSearchQuery(newValue);
                
                // Only perform real-time search for name/email search type
                if (searchType === 'name' && newValue.trim().length >= 2) {
                  // Clear any existing timeout
                  if (searchTimeoutRef.current) {
                    clearTimeout(searchTimeoutRef.current);
                  }
                  
                  // Set loading state
                  setLoading(true);
                  
                  // Perform search after short delay (debounce)
                  searchTimeoutRef.current = setTimeout(async () => {
                    try {
                      const token = localStorage.getItem('token');
                      
                      if (!token) {
                        setError('Authentication token not found');
                        setLoading(false);
                        return;
                      }
                      
                      const config = {
                        headers: {
                          'x-auth-token': token
                        },
                        params: {
                          search: newValue,
                          limit: 10
                        }
                      };
                      
                      const response = await axios.get('/api/leads', config);
                      
                      if (response.data && response.data.leads) {
                        setSearchResults(response.data.leads);
                        
                        if (response.data.leads.length === 0) {
                          setError('No leads found matching your search');
                        } else {
                          setError('');
                        }
                      }
                    } catch (error) {
                      console.error('Error searching leads:', error);
                      setError('Error searching leads');
                    } finally {
                      setLoading(false);
                    }
                  }, 300);
                } else if (newValue.trim().length < 2) {
                  // Clear results if search term is too short
                  setSearchResults([]);
                  if (newValue.trim().length > 0) {
                    setError('Please enter at least 2 characters');
                  } else {
                    setError('');
                  }
                }
              }}
              autoFocus
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder={
                searchType === 'id' 
                  ? 'Enter lead ID' 
                  : 'Type to search by name, email, or phone'
              }
            />
            {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
          </div>
          
          <div className="flex justify-end">
            {searchType === 'id' && (
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-400"
              >
                {loading ? 'Searching...' : 'Open in New Tab'}
              </button>
            )}
            {searchType === 'name' && loading && (
              <div className="px-4 py-2 text-sm text-blue-600">
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Searching...
                </span>
              </div>
            )}
          </div>
        </form>
        
        {/* Search results */}
        {searchResults.length > 0 && (
          <div className="mt-4 border-t pt-4">
            <h3 className="text-md font-medium text-gray-700 mb-2">Search Results</h3>
            <div className="max-h-60 overflow-y-auto">
              <ul className="divide-y divide-gray-200">
                {searchResults.map(lead => (
                  <li key={lead.id} className="py-2">
                    <button
                      onClick={() => handleLeadSelect(lead.id)}
                      className="w-full text-left px-2 py-1 hover:bg-blue-50 rounded-md"
                    >
                      <div className="font-medium text-blue-600">{lead.name}</div>
                      <div className="text-sm text-gray-600">{lead.email}</div>
                      {lead.phone && <div className="text-sm text-gray-600">{lead.phone}</div>}
                      <div className="text-xs text-gray-500">ID: {lead.id}</div>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OpenLeadByIdModal;